import pricingCatalog from './defaultPricingCatalog.json' with { type: 'json' };
import metadataCreditPolicy from '../../shared/metadataCreditPolicy.json' with { type: 'json' };
import metadataAnalysisPolicy from '../../shared/metadataAnalysisPolicy.json' with { type: 'json' };
import { getLanguageMarket } from '../locales/index.js';

// Public (prerendered) copy states these offers as numbers. They are owned by
// server code that the client bundle cannot import, so they are mirrored here
// and scripts/public-offer-facts-regression.mjs fails if the sources drift:
// - api/signup/complete.js WELCOME_CREDIT_AMOUNT (granted only when the signup
//   risk check passes, so copy must say "eligible new accounts")
// - server/_shared/referralRewards.js REFERRAL_CREDIT_AMOUNT (paid to the
//   referrer after an eligible signup completes the required checks)
// - server/_shared/vectorizerUsageLedger.js default DAILY_FREE_LIMIT and
//   PAID_IMAGE_CREDIT_COST (signed-in accounts only)
// - server/_ai/guest-metadata-proxy.js: one guest run per 24 hours, no signup
export const WELCOME_CREDIT_AMOUNT = 10;
export const REFERRAL_CREDIT_AMOUNT = 50;
export const VECTORIZER_FREE_DAILY_IMAGES = 10;
export const VECTORIZER_PAID_IMAGE_CREDITS = 1;
export const GUEST_DEMO_RUNS_PER_DAY = 1;

const byOrder = (left, right) => (Number(left.sortOrder) || 0) - (Number(right.sortOrder) || 0);

// Mirrors what /api/public-pricing can show: retired BYOK SKUs and hidden
// packages are never part of public copy. The live catalog can be overridden
// in Firestore (admin_settings/pricing_catalog); this bundled catalog is the
// build-time source the client also falls back to.
const PUBLIC_PACKAGES = (Array.isArray(pricingCatalog?.packages) ? pricingCatalog.packages : [])
  .filter((pkg) => pkg?.visible === true && pkg?.type !== 'byok');

const MONTHLY_PLANS = PUBLIC_PACKAGES
  .filter((pkg) => pkg.type === 'subscription' && pkg.billingPeriod === 'monthly')
  .sort(byOrder);
const YEARLY_PLANS = PUBLIC_PACKAGES
  .filter((pkg) => pkg.type === 'subscription' && pkg.billingPeriod === 'yearly')
  .sort(byOrder);

const roundCurrency = (value) => Math.round(Number(value) * 100) / 100;

export const PUBLIC_PRICING_PLANS = Object.freeze(MONTHLY_PLANS.map((monthly) => {
  const yearly = YEARLY_PLANS.find((pkg) => pkg.planName === monthly.planName && pkg.credits === monthly.credits);
  return Object.freeze({
    name: monthly.planName,
    credits: monthly.credits,
    monthlyPrice: roundCurrency(monthly.price),
    yearlyPrice: yearly ? roundCurrency(yearly.price) : null,
    yearlyPricePerMonth: yearly
      ? roundCurrency(yearly.pricePerMonth ?? (yearly.price / 12))
      : null,
    yearlyDiscountPercent: yearly ? Number(yearly.discount) || 0 : 0
  });
}));

const yearlyDiscounts = [...new Set(
  PUBLIC_PRICING_PLANS.filter((plan) => plan.yearlyPrice !== null).map((plan) => plan.yearlyDiscountPercent)
)];

// Only state a single "save N%" figure when every yearly plan shares it.
export const PUBLIC_YEARLY_DISCOUNT_PERCENT = yearlyDiscounts.length === 1 ? yearlyDiscounts[0] : null;

const topUpPackage = PUBLIC_PACKAGES
  .filter((pkg) => pkg.type === 'one-time')
  .sort(byOrder)[0];

export const PUBLIC_TOP_UP = topUpPackage
  ? Object.freeze({ credits: topUpPackage.credits, price: roundCurrency(topUpPackage.price) })
  : null;

const levelCost = (level, fallbackKey) => (
  Number(metadataAnalysisPolicy?.levels?.[level]?.creditCost)
  || Number(metadataCreditPolicy?.[fallbackKey])
);

export const PUBLIC_CREDIT_COSTS = Object.freeze({
  defaultLevel: metadataAnalysisPolicy?.defaultLevel || 'fast',
  fastImage: levelCost('fast', 'fastAnalysisImageCredits'),
  standardImage: levelCost('standard', 'standardImageCredits'),
  deepImage: levelCost('deep', 'deepAnalysisImageCredits'),
  videoWindowSeconds: Number(metadataCreditPolicy.videoCreditWindowSeconds),
  maxVideoSeconds: Number(metadataCreditPolicy.maxVideoDurationSeconds)
});

export const getLowestPublicPlan = () => PUBLIC_PRICING_PLANS[0] || null;
export const getHighestPublicPlan = () => PUBLIC_PRICING_PLANS[PUBLIC_PRICING_PLANS.length - 1] || null;

const getLocaleTag = (language = 'en') => getLanguageMarket(language)?.localeTag || 'en-US';

// Latin digits everywhere: buyers compare these numbers with checkout and
// with competitor pages, and AI readers parse them more reliably.
export const formatPublicUsd = (amount, language = 'en') => {
  const value = Number(amount);
  const hasCents = Math.round(value * 100) % 100 !== 0;
  return new Intl.NumberFormat(getLocaleTag(language), {
    style: 'currency',
    currency: 'USD',
    numberingSystem: 'latn',
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2
  }).format(value);
};

export const formatPublicCount = (value, language = 'en') => (
  new Intl.NumberFormat(getLocaleTag(language), { numberingSystem: 'latn' }).format(Number(value) || 0)
);
